import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import ToolPrepPopup, {
  ToolPrepCloseContext,
} from "../../../shared/Popup/ToolPrepPopup";
import ToolPrepDownloadFlowModals from "../../../shared/Popup/ToolPrepDownloadFlowModals";
import ToolPrepFlatResultStage from "../../../shared/Popup/ToolPrepFlatResultStage";
import OutlinePreview from "./OutlinePreview";
import Outline from "../Editor/Outline";
import ImagePickerModal from "../../../../../components/popups/imagePicker/ImagePickerModal";
import ToolPrepPreviewBgCards from "../../../shared/previewBg/ToolPrepPreviewBgCards";
import { useToolPrepImageSource } from "../../../hooks/useToolPrepImageSource";
import { useSyncScrollbarWidth } from "../../../hooks/useSyncScrollbarWidth";
import { useToolPrepBakedApplyFlow } from "../../../hooks/useToolPrepBakedApplyFlow";
import { useEditorStore } from "../../../../../state/editorStore";
import { ensureBackgroundRemoved } from "../../../../../utils/ensureBackgroundRemoved";
import { adoptBlobUrl } from "../../../../../utils/adoptBlobUrl";
import { exportOutlinePrepImageBlob } from "../../../lib/exportToolPrepEffectToPngBlob";
import { hasActiveOutlineSettings } from "../../../lib/toolPrepEffectActiveSettings";
import {
  OUTLINE_ENTRY_BACKGROUND,
  OUTLINE_ENTRY_DEFAULT,
  TOOL_PREP_PREVIEW_BG,
  TOOL_PREP_PREPARING_PREVIEW_LABEL,
  TOOL_PREP_RESULT_FOOTER_HINT,
  initialToolPrepPreviewBg,
} from "../../../../../utils/editorEntrySession";
import {
  TOOL_PREP_CUTOUT_REVEAL_MS,
  useDelayedImageReady,
  useImageNaturalSize,
} from "../../../lib/toolPrepImageLayout";
import "./OutlineToolPrep.css";

const OUTLINE_THANK_YOU_COPY = {
  titleDownloading: "Downloading…",
  titleComplete: "Download complete",
  leadDownloading: "Please wait — we’re saving your file to this device.",
  leadComplete: "Your file is saved on this computer or phone. You can find it in your Downloads folder.",
  previewAlt: "Outline image download",
  primaryActionLabel: "Add new image",
};

/**
 * Editing Tools → Outline prep: auto BG removal, shared preview BG cards, Object Outline controls.
 */
const OutlineToolPrep = ({
  originalImageUrl = null,
  imageFile = null,
  cachedCutoutUrl = null,
  onCutoutReady = null,
  onDone = () => {},
  onCancel = () => {},
}) => {
  const { getToken } = useAuth();
  const openedFromEditor = useContext(ToolPrepCloseContext) === "editor";
  const setOutline = useEditorStore((s) => s.setOutline);
  const objectOutline = useEditorStore((s) => s.object.outline);
  const [previewBg, setPreviewBg] = useState(() => initialToolPrepPreviewBg());

  const [cutoutUrl, setCutoutUrl] = useState(cachedCutoutUrl || null);
  const [creationId, setCreationId] = useState(null);
  const [bgLoading, setBgLoading] = useState(false);
  const [bgError, setBgError] = useState(null);
  const [doneBusy, setDoneBusy] = useState(false);
  const seededOutlineRef = useRef(false);
  const subjectPlacementRef = useRef(null);
  const bgStartedRef = useRef(false);
  const onCutoutReadyRef = useRef(onCutoutReady);
  const scrollBodyRef = useRef(null);
  const resetAppliedRef = useRef(() => {});
  onCutoutReadyRef.current = onCutoutReady;

  const {
    sourceImageUrl,
    resolvedFile,
    sourceFileName,
    sourceFileSizeLabel,
    clearSource,
    openUploadPicker,
    imagePickerProps,
    uploadProgress,
  } = useToolPrepImageSource({
    originalImageUrl,
    imageFile,
    busy: bgLoading || doneBusy,
    onSourceChange: () => {
      setCutoutUrl(null);
      setCreationId(null);
      setBgError(null);
      bgStartedRef.current = false;
      resetAppliedRef.current();
      subjectPlacementRef.current = null;
      setPreviewBg(initialToolPrepPreviewBg());
    },
  });

  const {
    applied: outlineApplied,
    busy: outlineBusy,
    resultUrl: outlinePreviewUrl,
    applyError: outlineApplyError,
    setApplyError: setOutlineApplyError,
    apply: applyOutline,
    resetApplied,
    downloadBusy,
    handleDownload,
    downloadFlow,
  } = useToolPrepBakedApplyFlow({
    sourceFileName,
    fallbackDownloadName: "outline.png",
    toolKey: "outline",
    shareTitle: "Outline tool",
    shareText: "I added an outline to my photo with LazyStudio's Outline tool!",
    thankYouCopy: OUTLINE_THANK_YOU_COPY,
  });

  resetAppliedRef.current = resetApplied;

  useEffect(() => {
    if (cachedCutoutUrl) setCutoutUrl(cachedCutoutUrl);
  }, [cachedCutoutUrl]);

  useEffect(() => {
    if (seededOutlineRef.current) return;
    seededOutlineRef.current = true;
    setOutline({ ...OUTLINE_ENTRY_DEFAULT }, { recordHistory: false });
  }, [setOutline]);

  const runBackgroundRemoval = useCallback(async () => {
    if (cutoutUrl?.startsWith("blob:") || cutoutUrl?.startsWith("data:")) {
      return { cutoutUrl, creationId };
    }
    if (
      cachedCutoutUrl?.startsWith("blob:") ||
      cachedCutoutUrl?.startsWith("data:")
    ) {
      setCutoutUrl(cachedCutoutUrl);
      return { cutoutUrl: cachedCutoutUrl, creationId };
    }

    setBgLoading(true);
    setBgError(null);
    try {
      const token = await getToken();
      const { cutoutUrl: nextUrl, creationId: cid } =
        await ensureBackgroundRemoved({
          token,
          imageFile: resolvedFile,
          imageUrl: sourceImageUrl,
          cachedCutoutUrl: cutoutUrl || cachedCutoutUrl,
        });
      setCutoutUrl(nextUrl);
      if (cid != null) setCreationId(cid);
      onCutoutReadyRef.current?.(nextUrl);
      return { cutoutUrl: nextUrl, creationId: cid ?? creationId };
    } catch (err) {
      setBgError(err?.message || "Failed to remove background");
      throw err;
    } finally {
      setBgLoading(false);
    }
  }, [
    cachedCutoutUrl,
    creationId,
    cutoutUrl,
    getToken,
    resolvedFile,
    sourceImageUrl,
  ]);

  useEffect(() => {
    if (bgStartedRef.current) return;
    if (!resolvedFile && !sourceImageUrl) return;
    bgStartedRef.current = true;
    runBackgroundRemoval().catch(() => {});
  }, [resolvedFile, runBackgroundRemoval, sourceImageUrl]);

  const previewImageUrl = cutoutUrl || null;
  const cutoutRevealReady = useDelayedImageReady(
    previewImageUrl,
    TOOL_PREP_CUTOUT_REVEAL_MS
  );
  const revealCutoutUrl = cutoutRevealReady ? previewImageUrl : null;
  const cutoutNaturalSize = useImageNaturalSize(revealCutoutUrl);
  // Match export pad: outline stroke bleed + blur (buildSubjectEffectBlob).
  const outlineBleedPx = Math.max(
    0,
    Math.ceil(
      (Number(objectOutline?.width) || 0) + (Number(objectOutline?.blur) || 0)
    )
  );

  const handlePreviewBgChange = (nextBg) => {
    resetApplied();
    setOutlineApplyError(null);
    setPreviewBg(nextBg);
  };

  const resolveBackgroundColor = () =>
    previewBg === TOOL_PREP_PREVIEW_BG.WHITE ||
    previewBg === TOOL_PREP_PREVIEW_BG.COLOR
      ? OUTLINE_ENTRY_BACKGROUND
      : null;

  /** Keep a durable original so the editor can restore the upload. */
  const resolveOriginalForEditor = async () => {
    if (!sourceImageUrl) return null;
    try {
      return await adoptBlobUrl(sourceImageUrl);
    } catch {
      return sourceImageUrl;
    }
  };

  const buildExport = useCallback(() => {
    const url = revealCutoutUrl;
    const outlineState = { ...objectOutline };
    return () =>
      exportOutlinePrepImageBlob(url, {
        shadowState: { enabled: false },
        outlineState,
        previewBg,
        backgroundColor: resolveBackgroundColor(),
        uploadedImageUrl: sourceImageUrl,
      });
  }, [objectOutline, previewBg, revealCutoutUrl, sourceImageUrl]);

  const handleApplyOutline = async () => {
    if (
      !revealCutoutUrl ||
      outlineBusy ||
      !hasActiveOutlineSettings(objectOutline)
    ) {
      return;
    }
    await applyOutline(buildExport());
  };

  const handleApplyToEditor = async () => {
    if (!revealCutoutUrl || !hasActiveOutlineSettings(objectOutline)) return;
    onDone({
      imageUrl: revealCutoutUrl,
      originalImageUrl: await resolveOriginalForEditor(),
      creationId,
      outline: { ...objectOutline },
      previewBg,
      backgroundColor: resolveBackgroundColor(),
      entryFlow: "outline",
    });
  };

  const handleAdjustOutline = () => {
    setOutlineApplyError(null);
    resetApplied();
  };

  const handleDone = async () => {
    setDoneBusy(true);
    try {
      const originalForEditor = await resolveOriginalForEditor();
      // Open in Editor: live cutout + outline + stage bg (same as Shadow).
      // The baked PNG is only for download. Sending it made the editor show
      // the transparent cutout instead of the Preview card on the tool canvas.
      if (outlineApplied && (cutoutUrl || revealCutoutUrl)) {
        onDone({
          imageUrl: cutoutUrl || revealCutoutUrl,
          originalImageUrl: originalForEditor,
          creationId,
          outline: { ...objectOutline },
          previewBg,
          backgroundColor: resolveBackgroundColor(),
          entryFlow: "outline",
        });
        return;
      }

      let imageUrl = cutoutUrl;
      let doneCreationId = creationId;
      if (!imageUrl) {
        const removed = await runBackgroundRemoval();
        imageUrl = removed?.cutoutUrl;
        doneCreationId = removed?.creationId ?? doneCreationId;
      }
      if (!imageUrl) {
        throw new Error("Background-removed image is not ready");
      }
      if (
        typeof imageUrl === "string" &&
        /^https?:\/\//i.test(imageUrl) &&
        !imageUrl.startsWith("blob:")
      ) {
        throw new Error(
          "Background-removed image could not be loaded for the editor (CORS). Please try again."
        );
      }
      onDone({
        imageUrl,
        originalImageUrl: originalForEditor,
        creationId: doneCreationId,
        outline: { ...objectOutline },
        previewBg,
        backgroundColor: resolveBackgroundColor(),
        entryFlow: "outline",
      });
    } catch (err) {
      setBgError(err?.message || "Could not finish outline setup");
    } finally {
      setDoneBusy(false);
    }
  };

  const canApplyOutline =
    Boolean(sourceImageUrl) &&
    Boolean(revealCutoutUrl) &&
    hasActiveOutlineSettings(objectOutline) &&
    !outlineBusy;

  const configureFooterHint = !sourceImageUrl
    ? "Upload an image to start adding an outline."
    : !revealCutoutUrl
      ? "Removing background…"
      : !hasActiveOutlineSettings(objectOutline)
        ? "Enable outline in the controls, then click Apply Outline."
        : "Adjust the outline, then click Apply Outline.";

  const leftContent = (
    <div className="otp-panel">
      <div className="otp-preview-bg-block">
        <ToolPrepPreviewBgCards
          previewBg={previewBg}
          onPreviewBgChange={handlePreviewBgChange}
          cutoutUrl={revealCutoutUrl}
          uploadedImageUrl={sourceImageUrl}
          cutoutPending={Boolean(sourceImageUrl) && !cutoutRevealReady}
          onUploadClick={openUploadPicker}
          onRemove={clearSource}
          uploadProgress={uploadProgress}
          sourceName={sourceFileName}
          sourceSizeLabel={sourceFileSizeLabel}
          actionsDisabled={bgLoading || doneBusy || downloadBusy}
          removeBgControls={{
            active: true,
            locked: true,
            bio: "Required — so the outline wraps the subject, not the photo backdrop.",
            error: bgError,
          }}
        />
      </div>

      <div
        className={`otp-outline-controls tool-prep-scroll${
          outlineApplied ? " otp-outline-controls--locked" : ""
        }`}
        ref={scrollBodyRef}
      >
        <Outline defaultExpanded forceExpanded />
      </div>
    </div>
  );

  useSyncScrollbarWidth(scrollBodyRef, [sourceImageUrl, cutoutUrl, outlineApplied]);

  return (
    <>
      <ImagePickerModal {...imagePickerProps} />
      <ToolPrepPopup
        toolKey="outline"
        title="Outline"
        left={leftContent}
        preview={
          outlineApplied ? (
            <ToolPrepFlatResultStage
              className="outline-tool-preview"
              resultImageUrl={outlinePreviewUrl}
              previewBg={previewBg}
              frameNaturalSize={cutoutNaturalSize}
              subjectNaturalSize={cutoutNaturalSize}
              shadowBleedPx={outlineBleedPx}
              subjectPlacement={subjectPlacementRef.current}
              applying={outlineBusy}
              applyingLabel="Applying outline…"
              resultAlt="Outline result"
              softWatermarkToolKey="outline"
              emptyLabel={
                uploadProgress != null && !sourceImageUrl
                  ? TOOL_PREP_PREPARING_PREVIEW_LABEL
                  : undefined
              }
            />
          ) : (
            <OutlinePreview
              imageUrl={revealCutoutUrl}
              uploadedImageUrl={sourceImageUrl}
              loading={Boolean(sourceImageUrl) && !cutoutRevealReady}
              applying={outlineBusy}
              applyingLabel="Applying outline…"
              previewBg={previewBg}
              showSubjectBox={Boolean(revealCutoutUrl)}
              onSubjectPlacement={(placement) => {
                subjectPlacementRef.current = placement;
              }}
              emptyLabel={
                uploadProgress != null && !sourceImageUrl
                  ? TOOL_PREP_PREPARING_PREVIEW_LABEL
                  : undefined
              }
            />
          )
        }
        onDone={
          openedFromEditor
            ? handleApplyToEditor
            : outlineApplied
              ? handleDone
              : handleApplyOutline
        }
        onCancel={onCancel}
        confirmClose={Boolean(sourceImageUrl)}
        onDownload={
          openedFromEditor
            ? null
            : outlineApplied
              ? () => handleDownload(buildExport())
              : null
        }
        onOpenInEditor={
          openedFromEditor ? null : outlineApplied ? handleDone : null
        }
        onSecondaryAction={
          openedFromEditor ? null : outlineApplied ? handleAdjustOutline : null
        }
        secondaryActionLabel="Adjust outline"
        busy={
          openedFromEditor
            ? Boolean(bgLoading && !cutoutUrl)
            : outlineBusy || doneBusy || (bgLoading && !cutoutUrl) || downloadBusy
        }
        busyLabel="Applying outline…"
        showDone={openedFromEditor || !outlineApplied}
        doneDisabled={!canApplyOutline}
        doneLabel="Apply Outline"
        footerActionOrder={
          outlineApplied
            ? ["cancel", "secondary", "download", "openInEditor"]
            : ["cancel", "primary"]
        }
        openInEditorLabel="Open in Editor"
        footerHint={
          outlineApplyError ||
          (outlineApplied ? TOOL_PREP_RESULT_FOOTER_HINT : configureFooterHint)
        }
        footerHintClassName={
          outlineApplyError ? "tool-prep-popup__footer-hint--error" : ""
        }
      />
      <ToolPrepDownloadFlowModals
        flow={downloadFlow}
        toolKey="outline"
        onPrimaryAction={() => {
          downloadFlow.handleThankYouDismiss();
          clearSource();
        }}
        onBackToDashboard={onCancel}
      />
    </>
  );
};

export default OutlineToolPrep;
